import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, inject } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { DepositConfirmationData, DepositContainerComponent } from './deposit-container/deposit-container.component';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { WalletDto } from '@olv/ui-shared/models/wallet.model';
import { Merchant } from '@olv/ui-shared/models/merchant.model';

@Component({
  selector: 'app-merchant-deposit',
  standalone: true,
  imports: [DepositContainerComponent, HasPermissionDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './merchant-deposit.component.html',
})
export class MerchantDepositComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;

  // Input properties from parent component
  @Input() wallets: WalletDto[] = [];
  @Input() merchant?: Merchant;
  @Input() isLoading = false;
  @Input() error: string | null = null;

  // Merchant info
  merchantId = '';

  // Cleanup subscription
  private destroy$ = new Subject<void>();
  selectedBlockchainType: number | null = null;
  filteredWallets: WalletDto[] = [];
  searchText = '';
  selectedWalletId: string | null = null;

  ngOnInit(): void {
    // Get merchant ID from route parameter or other source
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.merchantId = id;
      } else {
        // Fallback in case there's no route parameter
        this.getMerchantIdFromParent();
      }
    });
  }

  /**
   * Alternative method to get merchantId if not available in route
   */
  private getMerchantIdFromParent(): void {
    // In a real app, this might come from a state management service
    // For this example, we'll use a fixed ID for demonstration
    // You would replace this with the actual implementation
    this.merchantId = '12345'; // Example merchant ID
  }

  /**
   * Handle search input changes
   */
  onSearchChange(value: string): void {
    this.searchText = value;
    this.applyFilter();
  }

  /**
   * Apply search filter to wallets
   */
  private applyFilter(): void {
    // Start with all wallets
    let filtered = [...this.wallets];

    // Apply blockchain filter if one is selected
    if (this.selectedBlockchainType !== null) {
      filtered = filtered.filter((wallet) => wallet.networkType === this.selectedBlockchainType);
    }

    // Then apply text search filter if there is search text
    if (this.searchText) {
      const search = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(
        (wallet) =>
          wallet.address.toLowerCase().includes(search) ||
          (wallet.merchantName && wallet.merchantName.toLowerCase().includes(search)),
      );
    }

    this.filteredWallets = filtered;
  }


  /**
   * Handle deposit completion from the shared component
   */
  onDepositCompleted(data: DepositConfirmationData): void {
    console.log('Deposit completed:', data);
    // Handle any merchant-specific logic here
  }

  /**
   * Handle wallet creation events
   */
  onWalletCreated(walletId: string): void {
    console.log('Wallet created with ID:', walletId);
    // Handle any merchant-specific logic here
  }

  /**
   * Gets the currently selected wallet object
   * @returns The selected wallet object or undefined if no wallet is selected
   */
  getSelectedWallet(): WalletDto | undefined {
    if (this.selectedWalletId === null) {
      return undefined;
    }

    return this.wallets.find((wallet) => wallet.id === this.selectedWalletId);
  }

  onNewDepositRequested(): void {
    console.log('New deposit requested');
    // Handle any merchant-specific logic here
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
